import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import classnames from 'classnames';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import {
  Input,
  UncontrolledPopover,
  PopoverBody,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  addSelectedFile,
  removeSelectedFile,
} from '../../actions/Files';

import FileActions from '../File/Actions';
import FileDetails from '../File/Details';
import GridItemLikes from '../GridItemLikes';
import FileTitle from '../File/Title';
import FileStatus from '../File/Status';
import TrashRemainingDays from '../TrashRemainingDays';

import { userCan } from '../../helpers/Auth';
import {
  filesTarget,
  filesSource,
} from '../../helpers/DNDFiles';
import ItemTypes from '../../helpers/DNDItemTypes';
import { getFilePageUrl } from '../../helpers/Files';

class FilesTileItem extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      containerWidth: 0,
    };

    this.containerRef = React.createRef();
  }

  componentDidMount() {
    const { connectDragPreview } = this.props;

    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true,
      });
    }

    this.getContainerWidth();
  }

  componentDidUpdate() {
    this.getContainerWidth();
  }

  getContainerWidth() {
    if (this.containerRef) {
      this.setState({ containerWidth: this.containerRef.offsetWidth });
    }
  }

  render() {
    const {
      intl,
      file,
      collection,
      shareUuid,
      shareCollectionUuid,
      share,
      connectDragSource,
      connectDropTarget,
      isDragging,
      isSelected,
      imageProps,
      onFileSelect,
      displayOnly,
      allowCheck,
      renderDisplayAction,
      overwriteUrl,
    } = this.props;
    const { containerWidth } = this.state;
    const imageSrc = (typeof overwriteUrl === 'function' && overwriteUrl(file)) || file.urls.thumbnailmasonry;

    const fileTo = file?.to
      || getFilePageUrl({ fileUuid: file.uuid, shareUuid, shareCollectionUuid });

    const pointToNone = !!file.pointToNone;
    const hideMeta = !!file.hideMeta;

    const statusEditable = share ? share.status_change : userCan('file.edit', file);

    return connectDragSource(connectDropTarget((
      <div
        id={file.uuid}
        className={classnames(
          'masonry-item',
          'tile-item',
          {
            dragging: isDragging,
            'tile-item-point-to-none': pointToNone,
          },
        )}
        style={imageProps.style}
        ref={(ref) => { this.containerRef = ref; }}
      >
        <div className="file-controls">
          {!displayOnly && (
          <>
            <TrashRemainingDays
              file={file}
            />
            {!(!file.status && !statusEditable) && (
            <div className="file-controls-status">
              <FileStatus
                file={file}
                alwaysShowStatus={true}
              />
            </div>
            )}
            <GridItemLikes
              file={file}
              share={share}
              readOnly={!!file.deleted_at}
            />
          </>
          )}
          {allowCheck && (!shareUuid || (share && share.allow_download)) && (
            <Input
              type="checkbox"
              className="input-check check-l"
              id={`file-${file.uuid}`}
              checked={isSelected}
              onChange={() => onFileSelect(file.uuid, isSelected)}
              aria-label={isSelected ? intl.formatMessage({ defaultMessage: 'Unselect' }) : intl.formatMessage({ defaultMessage: 'Select' })}
            />
          )}
        </div>

        <div id={`file-element-${file.uuid}`} className={classnames('masonry-item-wrapper tile-item-wrapper', { checked: isSelected })}>
          {pointToNone && (
            <div className="masonry-item-img-wrapper masonry-item-img-wrapper-point-to-none">
              <img
                className="masonry-item-img"
                src={imageSrc}
                alt={file.display_name}
              />
            </div>
          )}
          {!pointToNone && (
            <>
              {(file.type === 'link' && !file.html_code) ? (
                <a href={file.path} className="masonry-item-img-wrapper" title={file.display_name} target="_blank" rel="noopener noreferrer">
                  <img
                    className="masonry-item-img"
                    src={imageSrc}
                    alt={file.display_name}
                  />
                </a>
              ) : (
                <Link to={fileTo} className="masonry-item-img-wrapper">
                  <img
                    className="masonry-item-img"
                    src={imageSrc}
                    alt={file.display_name}
                  />
                </Link>
              )}
            </>
          )}
          {(!pointToNone && !hideMeta) && (
            <div className="masonry-item-body">
              <div className="masonry-item-header tile-item-header">
                {containerWidth > 240 ? (
                  <>
                    <FileTitle
                      className="masonry-item-title"
                      file={file}
                      fileTo={fileTo}
                    />
                    <FileDetails
                      className="masonry-item-details"
                      file={file}
                      showAccess={!shareUuid && !displayOnly}
                    />
                  </>
                ) : (
                  <>
                    <UncontrolledPopover
                      className="file-popover"
                      container={`#file-element-${file.uuid}`}
                      trigger="hover"
                      fade={false}
                      positionFixed={true}
                      target={`file-popover-${file.uuid}`}
                    >
                      <PopoverBody>
                        <FileTitle
                          className="masonry-item-title"
                          file={file}
                          fileTo={fileTo}
                          inPopover={true}
                        />
                        <FileDetails
                          className="masonry-item-details"
                          file={file}
                          showAccess={!shareUuid && !displayOnly}
                        />
                      </PopoverBody>
                    </UncontrolledPopover>
                    <span>
                      <i className="far fa-circle-info" id={`file-popover-${file.uuid}`} />
                    </span>
                  </>
                )}
              </div>
              <div className="masonry-item-actions tile-item-actions">
                {(!displayOnly || renderDisplayAction) && (
                <ul className="grid-item-actions-list">
                  {!displayOnly && (
                    <li className="action-more">
                      <FileActions
                        file={file}
                        collection={collection}
                        shareUuid={shareUuid}
                        share={share}
                        filesView="tile"
                      />
                    </li>
                  )}
                  {displayOnly && renderDisplayAction && renderDisplayAction(file, 'tile')}
                </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )));
  }
}

const mapStateToProps = state => ({
  collection: state.Collections.collection,
});

const mapDispatchToProps = dispatch => ({
  selectFile: (file) => {
    dispatch(addSelectedFile(file));
  },
  unselectFile: (fileUuid) => {
    dispatch(removeSelectedFile(fileUuid));
  },
});

FilesTileItem.propTypes = {
  intl: PropTypes.object.isRequired,
  file: PropTypes.object.isRequired,
  collection: PropTypes.object,
  shareUuid: PropTypes.string,
  share: PropTypes.object,
  shareCollectionUuid: PropTypes.string,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  imageProps: PropTypes.object.isRequired,
  onFileSelect: PropTypes.func.isRequired,
  displayOnly: PropTypes.bool,
  allowCheck: PropTypes.bool,
  renderDisplayAction: PropTypes.func,
  overwriteUrl: PropTypes.func,
};

FilesTileItem.defaultProps = {
  collection: null,
  shareUuid: null,
  share: null,
  shareCollectionUuid: null,
  displayOnly: false,
  allowCheck: true,
  renderDisplayAction: null,
  overwriteUrl: null,
};

export default
injectIntl(withRouter(connect(mapStateToProps, mapDispatchToProps)(
  DragSource(ItemTypes.FILE, filesSource, (DNDconnect, monitor) => ({
    connectDragSource: DNDconnect.dragSource(),
    connectDragPreview: DNDconnect.dragPreview(),
    isDragging: monitor.isDragging(),
  }))(DropTarget([ItemTypes.FILE, ItemTypes.COLLECTION], filesTarget, DNDconnect => ({
    connectDropTarget: DNDconnect.dropTarget(),
  }))(FilesTileItem)),
)));
